import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { 
  Lock, 
  ShoppingCart,
  School,
  Person
} from '@material-ui/icons';
// Removed old monetization service imports
import './AccessControl.css';

const AccessControl = ({ 
  bookId, 
  bookTitle, 
  bookPrice = 0,
  isAuthenticated, 
  userRole,
  user,
  onPurchaseSuccess,
  onSubscriptionSuccess,
  variant = 'card',
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);

  useEffect(() => {
    checkAccessStatus();
  }, [bookId, isAuthenticated, userRole, user]);

  const checkAccessStatus = async () => {
    setLoading(true);
    
    try {
      console.log('🔍 AccessControl: Checking access status...');
      console.log('🔍 AccessControl: User object:', user);
      console.log('🔍 AccessControl: Student data:', user?.student);
      console.log('🔍 AccessControl: hasFullLibraryAccess:', user?.student?.hasFullLibraryAccess);
      console.log('🔍 AccessControl: subscriptionStatus:', user?.student?.subscriptionStatus);
      
      if (isAuthenticated) {
        // Check if student is from Université de la Formation Continue (U.F.C)
        // Check both user.student.establishment and user.establishment
        const studentEstablishment = user?.student?.establishment?.name || user?.student?.establishment;
        const userEstablishment = user?.establishment?.name || user?.establishment;
        const establishmentName = studentEstablishment || userEstablishment || userRole;
        
        const isUFCStudent = establishmentName && (
          establishmentName.includes('U.F.C') || 
          establishmentName.includes('Université de la Formation Continue') ||
          establishmentName.includes('Formation Continue') ||
          establishmentName.includes('UFC')
        );
        
        if (isUFCStudent) {
          console.log('✅ AccessControl: UFC student - granting free access');
          setAccessStatus('free_access');
          return;
        }
        
        // Check if student has full access via any method
        // Access types:
        // 1. verified_and_paid - paid via Progres API
        // 2. verified_access - contract-based access (establishment contract)
        // 3. hasFullLibraryAccess: true - any method that grants full access
        const hasFullLibraryAccess = user?.student?.hasFullLibraryAccess || false;
        const subscriptionStatus = user?.student?.subscriptionStatus || 'unverified_or_unpaid';
        
        console.log('🔍 AccessControl: Subscription check - hasFullAccess:', hasFullLibraryAccess, 'status:', subscriptionStatus);
        
        if (hasFullLibraryAccess || 
            subscriptionStatus === 'verified_and_paid' || 
            subscriptionStatus === 'verified_access') {
          // Student has full access (paid OR contract-based) - show full access
          const accessType = subscriptionStatus === 'verified_and_paid' 
            ? 'PAID subscription via Progres' 
            : subscriptionStatus === 'verified_access'
            ? 'CONTRACT-based access via establishment'
            : 'FULL ACCESS (any method)';
          console.log(`✅ AccessControl: Student has full access: ${accessType}`);
          setAccessStatus('subscription_access');
          return;
        }
        
        // For students without verified subscription, show subscription required
        console.log('⚠️ AccessControl: No verified subscription - showing subscription required');
        setAccessStatus('subscription_required');
        return;
      }

      // For non-authenticated users with non-test books, check if book has price
      if (!isAuthenticated) {
        if (bookPrice > 0) {
          setAccessStatus('purchase_info');
          return;
        } else {
          setAccessStatus('login_required');
          return;
        }
      }
    } catch (error) {
      console.error('❌ Error checking access status:', error);
      setAccessStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const goBuy = () => {
    window.location.href = `/visitor/books?bookId=${bookId}`;
  };

  const renderAccessContent = () => {
    if (loading) {
      return variant === 'inline' ? null : (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80px">
          <CircularProgress size={22} />
        </Box>
      );
    }

    if (variant === 'inline') {
      if (accessStatus === 'purchase_info' || accessStatus === 'purchase_required') {
        return (
          <button type="button" className="bd-buy" onClick={goBuy}>
            {t('monetization.individual.purchase') || 'Acheter'} · {bookPrice} DA
          </button>
        );
      }
      if (accessStatus === 'login_required') {
        return (
          <a className="bd-buy is-ghost" href="/login">
            {t('login.form.connect')}
          </a>
        );
      }
      if (accessStatus === 'subscription_required') {
        return (
          <a
            className="bd-buy is-ghost"
            href={t('login.steps-to-register.subscriptionUrl')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('login.steps-to-register.subscriptionLink')}
          </a>
        );
      }
      return null;
    }

    switch (accessStatus) {
      case 'purchase_info':
        return (
          <Card className="access-card purchase-info">
            <CardContent style={{ padding: '12px 16px' }}>
              <Box display="flex" alignItems="center" mb={1}>
                <ShoppingCart className="access-icon" />
                <Typography variant="h6" className="access-title">
                  Livre disponible à l'achat
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: '14px', marginBottom: '8px' }}>
                Ce livre est disponible à l'achat individuel
              </Typography>
              <Box mt={1} display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" className="subscription-price">
                  {bookPrice} DA
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: '12px' }}>
                  Paiement: Dahabia
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center">
                <button 
                  className="purchase-button-compact"
                  onClick={() => {
                    // For all books with prices, redirect to visitor payment page with book pre-selected
                    // The BookPurchase component will handle adding the book to cart
                    const visitorPaymentUrl = `/visitor/books?bookId=${bookId}`;
                    window.location.href = visitorPaymentUrl;
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(225, 42, 47, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(225, 42, 47, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(225, 42, 47, 0.3)';
                  }}
                >
                  <i className="fas fa-shopping-cart me-1"></i>
                  Acheter maintenant
                </button>
              </Box>
            </CardContent>
          </Card>
        );

      case 'login_required':
        return (
          <Card className="access-card login-required">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Person className="access-icon" />
                <Typography variant="h6" className="access-title">
                  {t('monetization.access.loginRequired')}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {t('monetization.access.loginRequired')}
              </Typography>
            </CardContent>
          </Card>
        );

      case 'subscription_access':
        return (
          <Card className="access-card subscription-access">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <School className="access-icon success" />
                <Typography variant="h6" className="access-title success">
                  {t('monetization.subscription.subscribed')}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                You have full access to all books with your active subscription.
              </Typography>
            </CardContent>
          </Card>
        );

      case 'purchased_access':
        return (
          <Card className="access-card purchased-access">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ShoppingCart className="access-icon success" />
                <Typography variant="h6" className="access-title success">
                  {t('monetization.individual.purchased')}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                You have purchased this book and have full access.
              </Typography>
            </CardContent>
          </Card>
        );

      case 'purchase_required':
        return (
          <Card className="access-card purchase-required">
            <CardContent style={{ padding: '12px 16px' }}>
              <Box display="flex" alignItems="center" mb={1}>
                <ShoppingCart className="access-icon" />
                <Typography variant="h6" className="access-title">
                  Livre disponible à l'achat
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: '14px', marginBottom: '8px' }}>
                Ce livre est disponible à l'achat individuel
              </Typography>
              <Box display="flex" justifyContent="center">
                <button 
                  className="purchase-button-compact"
                  onClick={() => {
                    // Redirect to visitor payment page with book pre-selected
                    const visitorPaymentUrl = `/visitor/books?bookId=${bookId}`;
                    window.location.href = visitorPaymentUrl;
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #e12a2f 0%, #c41e3a 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(225, 42, 47, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(225, 42, 47, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(225, 42, 47, 0.3)';
                  }}
                >
                  <i className="fas fa-shopping-cart me-1"></i>
                  Acheter maintenant
                </button>
              </Box>
            </CardContent>
          </Card>
        );

      case 'subscription_required':
        return (
          <Card className="access-card subscription-required">
            <CardContent style={{ padding: '12px 16px' }}>
              <Box display="flex" alignItems="center" mb={1}>
                <School className="access-icon" />
                <Typography variant="h6" className="access-title">
                  {t('monetization.subscription.required')}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: '14px', marginBottom: '8px' }}>
                {t('monetization.subscription.message')}
              </Typography>
              <Box mt={1}>
                <Typography variant="h6" color="primary" className="subscription-price">
                  {t('monetization.subscription.price')}
                </Typography>
                <Box mt={1} display="flex" justifyContent="center">
                  <a 
                    href={t('login.steps-to-register.subscriptionUrl')} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      background: 'linear-gradient(135deg, #4b8b3b 0%, #3a6b2d 100%)',
                      color: 'white',
                      textDecoration: 'none',
                      padding: '8px 20px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(75, 139, 59, 0.3)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'inline-block'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(75, 139, 59, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(75, 139, 59, 0.3)';
                    }}
                  >
                    <i className="fas fa-credit-card me-1"></i>
                    {t('login.steps-to-register.subscriptionLink')}
                  </a>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );

      case 'free_access':
        return (
          <Card className="access-card free-access">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <School className="access-icon" />
                <Typography variant="h6" className="access-title">
                  {t('monetization.access.freeAccess')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        );

      case 'error':
        return (
          <Alert severity="error" className="access-alert">
            <Typography variant="body2">
              Error checking access status. Please try again.
            </Typography>
          </Alert>
        );

      default:
        return null;
    }
  };

  return variant === 'inline' ? (
    renderAccessContent()
  ) : (
    <Box className="access-control-container">{renderAccessContent()}</Box>
  );
};

export default AccessControl;
